import React, { useContext, useEffect, useRef, useState } from "react";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { useAuth } from "../../contexts/AuthContext";
import { useUserProfile } from "../../contexts/UserProfileContext";
import { useToast } from "../../contexts/ToastContext";
import api from "../../services/api";
import useWorkoutSettings from "../../hooks/useWorkoutSettings";
import DetailSheet from "../session/generate/DetailSheet";
import DraggableStepper from "../ui/draggable-stepper";
import {
  DEFAULT_RESTING_HR,
  estimateMaxHr,
  heartRateBasis,
  zoneFloors,
} from "../../utils/heartRateZones";
import SegmentModeToggle from "../ui/segment-mode-toggle";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { X } from "lucide-react";
import WeeklyTargetSummary from "./WeeklyTargetSummary";
import { ZoneTable } from "./CardioSheets";
import { CHANGEOVER_OPTIONS } from "../settings/settingsSummaries";
import { fromDisplayWeight, toDisplayWeight, weightUnitOf } from "../../utils/units";
import UnitToggle from "../ui/UnitToggle";
import { ChipGrid, ChoiceChip, Field, OptionList, SwitchRow } from "./ProfileParts";
import { GOAL_CATEGORIES } from "../../config/goals";
import { archetypeName } from "../../utils/mixedStyle";
import { resolveSegmentMode, segmentFlagsForMode } from "../../utils/workoutPlanUtils";
import {
  DEFAULT_REST_STYLE,
  DEFAULT_SESSION_PACE,
  MAX_TRAINING_PREFERENCES_LENGTH,
  REST_STYLE_OPTIONS,
  SESSION_PACE_OPTIONS,
} from "../../config/trainingPreferences";
import {
  FITNESS_LEVELS,
  REP_SPEEDS,
  TRAINING_EXPERIENCE,
} from "./profileSummaries";

const SEGMENT_HINT = {
  off: (name) => `Never add a ${name}.`,
  on: () => "Always add one, at a fixed length.",
  auto: () => "The AI sizes it, and leaves it out of short sessions.",
};

/**
 * The sheet behind each single-setting row on Profile. Profile fields are
 * edited on a local copy and saved once when the sheet closes: every profile
 * save goes to the server, and the server's reply wins when it is merged
 * back, so saving on each tap or keystroke could undo a later change.
 * Coach settings (history, changeovers) and rep speed are separate stores
 * with no merge-back, so those save straight away.
 */
const ProfileSettingsSheet = ({ kind, onClose }) => {
  const { userProfile, setUserProfile } = useContext(WorkoutContext);
  const { user, isAuthenticated, setCurrentUser } = useAuth();
  const { profile: cloudProfile, updateProfile, syncing } = useUserProfile();
  const toast = useToast();
  const [workoutSettings, updateWorkoutSettings] = useWorkoutSettings({
    onSync: (fields) => {
      if (isAuthenticated) api.updateSettings(fields).catch(console.error);
    },
  });

  // Keep the last sheet's content while it animates closed.
  const [shown, setShown] = useState(kind);
  useEffect(() => {
    if (kind) setShown(kind);
  }, [kind]);

  const [patch, setPatch] = useState({});
  const [name, setName] = useState("");
  const draft = { ...userProfile, ...patch };
  const update = (fields) => setPatch((prev) => ({ ...prev, ...fields }));

  // The latest values for the save on close, including an unmount while open.
  const pending = useRef({});
  pending.current = { patch, userProfile, name };

  useEffect(() => {
    if (kind === "account") setName(user?.displayName || user?.name || "");
  }, [kind, user?.displayName, user?.name]);

  const save = () => {
    const { patch: p, userProfile: base, name: n } = pending.current;
    if (Object.keys(p).length > 0) setUserProfile({ ...base, ...p });
    setPatch({});
    if (kind === "account" && isAuthenticated) {
      const trimmed = n.trim();
      const current = user?.displayName || user?.name || "";
      if (trimmed && trimmed !== current) {
        api
          .updateCurrentUser({ displayName: trimmed })
          .then(setCurrentUser)
          .catch((error) =>
            toast.showToast({
              title: "Name not saved",
              description: error.message || "Khronos couldn't update your account. Try again.",
              duration: 5000,
            }),
          );
      }
    }
  };
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => () => saveRef.current(), []);

  const close = () => {
    save();
    onClose();
  };

  const pace = cloudProfile?.training_data?.pace || "standard";
  const setRepSpeed = (value) =>
    updateProfile({
      training_data: { ...(cloudProfile?.training_data || {}), pace: value },
    }).catch(() =>
      toast.showToast({ title: "Rep speed not saved", description: "Check your connection and try again.", duration: 4000 }),
    );

  const sex = cloudProfile?.training_data?.sex || null;
  const setSex = (value) => {
    const { sex: _old, ...rest } = cloudProfile?.training_data || {};
    updateProfile({ training_data: value ? { ...rest, sex: value } : rest }).catch(() =>
      toast.showToast({ title: "Not saved", description: "Check your connection and try again.", duration: 4000 }),
    );
  };

  let title = "";
  let description = "";
  let body = null;

  switch (shown) {
    case "account":
      title = "Account";
      body = isAuthenticated ? (
        <>
          <Field label="Display name" htmlFor="profile-display-name" hint="Shown to friends and in groups.">
            <Input
              id="profile-display-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="How your name should appear"
            />
          </Field>
          <Field label="Email">
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </Field>
          <div className="rounded-lg border border-border bg-muted/20 px-3 py-2 text-sm">
            <span className="font-semibold text-green-500">{syncing ? "Syncing…" : "Synced"}</span>
            <p className="mt-0.5 text-xs leading-snug text-muted-foreground">
              Your profile, settings, activity and places sync to your account. This device also
              keeps a copy, so you can train offline.
            </p>
          </div>
        </>
      ) : (
        <p className="text-sm leading-snug text-muted-foreground">
          You're using Khronos on this device only. Sign in from the account menu to sync your
          profile, settings, activity and places across devices.
        </p>
      );
      break;

    case "level": {
      title = "Age & level";
      const year = draft.birthYear ? Number(draft.birthYear) : null;
      const age = year > 1900 ? new Date().getFullYear() - year : null;
      body = (
        <>
          <Field label="Birth year" htmlFor="profile-birth-year" aside={age != null ? `age ${age}` : null}>
            <Input
              id="profile-birth-year"
              type="number"
              inputMode="numeric"
              min="1900"
              max={new Date().getFullYear()}
              value={draft.birthYear || ""}
              onChange={(e) => update({ birthYear: e.target.value ? Number(e.target.value) : null })}
              placeholder="e.g. 1988"
            />
          </Field>
          <Field label="Fitness level" aside="how fit you are now">
            <ChipGrid
              columns={3}
              options={FITNESS_LEVELS}
              value={draft.fitnessLevel}
              onChange={(v) => update({ fitnessLevel: v })}
            />
          </Field>
          <Field label="Training experience" aside="how long you've trained">
            <ChipGrid
              columns={2}
              options={TRAINING_EXPERIENCE}
              value={draft.trainingExperience || "beginner"}
              onChange={(v) => update({ trainingExperience: v })}
            />
          </Field>
        </>
      );
      break;
    }

    case "goal": {
      title = "Goal";
      description = "Your usual goal. Programmes and Generate can set a different one.";
      const known = Object.values(GOAL_CATEGORIES).flat();
      body = (
        <>
          {draft.primaryGoal && !known.includes(draft.primaryGoal) && (
            <Field label="Current">
              <div className="flex flex-wrap gap-1.5">
                <ChoiceChip selected>{draft.primaryGoal}</ChoiceChip>
              </div>
            </Field>
          )}
          {Object.entries(GOAL_CATEGORIES).map(([category, goals]) => (
            <Field key={category} label={category}>
              <ChipGrid
                columns={2}
                options={goals.map((g) => ({ value: g, label: g }))}
                value={draft.primaryGoal}
                onChange={(v) => update({ primaryGoal: v })}
              />
            </Field>
          ))}
        </>
      );
      break;
    }

    case "days":
      title = "Days a week";
      body = (
        <>
          <DraggableStepper
            label="Days a week"
            value={draft.weeklySessions || 0}
            onChange={(v) => update({ weeklySessions: v })}
            min={0}
            max={14}
            step={1}
            unit="days"
            offLabel="Auto"
          />
          <WeeklyTargetSummary
            profile={draft}
            className="rounded-lg border border-border bg-muted/20 px-3 py-2"
          />
        </>
      );
      break;

    case "heart": {
      // Heart-rate zones (#94): each value shows its estimate until changed.
      const basis = heartRateBasis(draft);
      const estimatedMax = estimateMaxHr(draft.birthYear);
      const floors = basis ? zoneFloors(basis) : null;
      title = "Heart rate & zones";
      description = "Your zones come from these two numbers. Leave them estimated if you don't know them.";
      body = (
        <>
          <div className="grid gap-1.5">
            <DraggableStepper
              label="Max heart rate"
              value={draft.maxHeartRate || estimatedMax || 180}
              onChange={(v) => update({ maxHeartRate: v })}
              min={100}
              max={230}
              step={1}
              unit="bpm"
            />
            <p className="text-center text-xs text-muted-foreground">
              {draft.maxHeartRate ? (
                <button type="button" className="underline underline-offset-2" onClick={() => update({ maxHeartRate: 0 })}>
                  Use the estimate{estimatedMax ? ` (${estimatedMax})` : ""}
                </button>
              ) : estimatedMax ? (
                "Estimated from your age. The highest you've seen on a hard effort is better."
              ) : (
                "Add your birth year in You for an estimate, or enter yours."
              )}
            </p>
          </div>
          <div className="grid gap-1.5">
            <DraggableStepper
              label="Resting heart rate"
              value={draft.restingHeartRate || DEFAULT_RESTING_HR}
              onChange={(v) => update({ restingHeartRate: v })}
              min={30}
              max={110}
              step={1}
              unit="bpm"
            />
            <p className="text-center text-xs text-muted-foreground">
              {draft.restingHeartRate ? (
                <button type="button" className="underline underline-offset-2" onClick={() => update({ restingHeartRate: 0 })}>
                  Use the typical {DEFAULT_RESTING_HR}
                </button>
              ) : (
                `A typical ${DEFAULT_RESTING_HR}. Yours is best measured lying down just after waking.`
              )}
            </p>
          </div>
          {/* How zones are worked out (#194 follow-up). */}
          <Field label="Zones from">
            <ChipGrid
              columns={2}
              options={[
                { value: "reserve", label: "Heart-rate reserve" },
                { value: "max", label: "% of max" },
              ]}
              value={draft.hrZoneMethod === "max" ? "max" : "reserve"}
              onChange={(v) => update({ hrZoneMethod: v })}
            />
          </Field>
          <p className="-mt-2 text-xs leading-snug text-muted-foreground">
            {draft.hrZoneMethod === "max"
              ? "% of max: zones are fractions of your max heart rate. Simpler, and what many watches use."
              : "Heart-rate reserve: zones sit between your resting and max heart rate, so they fit you more closely. Recommended."}
          </p>
          {floors ? (
            <ZoneTable floors={floors} />
          ) : (
            <p className="text-sm text-muted-foreground">Add your birth year in You for an estimate, or set your max above.</p>
          )}
          <p className="text-xs leading-relaxed text-muted-foreground">
            Zone 2 is the easy aerobic work most of your cardio should be. The same colours show on your charts and heart
            rate during a session. Your max rises by itself when a strap shows a higher one.
          </p>
        </>
      );
      break;
    }

    case "injuries":
      title = "Injuries & limitations";
      description = "The AI works around these in every session.";
      body = (
        <Field>
          <Textarea
            id="profile-injuries"
            aria-label="Injuries and limitations"
            value={draft.injuryNotes || ""}
            onChange={(e) => update({ injuryNotes: e.target.value })}
            rows={4}
            placeholder="e.g. Bad left knee, recovering from shoulder surgery"
          />
        </Field>
      );
      break;

    case "time":
      title = "Session length";
      description = "Where Generate starts. You can change it for any session.";
      body = (
        <DraggableStepper
          prominent
          momentum
          unit="min"
          ariaLabel="Time available"
          id="profile-session-length"
          value={draft.timeAvailable || 45}
          onChange={(v) => update({ timeAvailable: v })}
          min={15}
          max={120}
        />
      );
      break;

    case "warmcool": {
      title = "Warm-up & cool-down";
      const segment = (seg, label, durationKey, preferredKey, thresholdKey, fixedRange, aimMax) => {
        const mode = resolveSegmentMode(draft, seg);
        const lower = label.toLowerCase();
        return (
          <Field label={label}>
            <SegmentModeToggle
              id={`profile-${seg}-mode`}
              value={mode}
              onChange={(m) => update(segmentFlagsForMode(m, seg))}
              className="w-full"
            />
            <p className="text-xs leading-snug text-muted-foreground">{SEGMENT_HINT[mode](lower)}</p>
            {mode === "on" && (
              <DraggableStepper
                label="Length"
                value={draft[durationKey] || 5}
                onChange={(v) => update({ [durationKey]: v })}
                min={fixedRange[0]}
                max={fixedRange[1]}
                unit="min"
              />
            )}
            {mode === "auto" && (
              <div className="grid grid-cols-2 gap-3">
                <DraggableStepper
                  label="Aim for"
                  value={draft[preferredKey] ?? 0}
                  onChange={(v) => update({ [preferredKey]: v })}
                  min={0}
                  max={aimMax}
                  step={5}
                  unit="min"
                  offLabel="AI decides"
                />
                <DraggableStepper
                  label="If session is at least"
                  value={draft[thresholdKey] ?? 30}
                  onChange={(v) => update({ [thresholdKey]: v })}
                  min={0}
                  max={60}
                  step={5}
                  unit="min"
                  offLabel="Always"
                />
              </div>
            )}
          </Field>
        );
      };
      body = (
        <>
          {segment("warmup", "Warm-up", "warmupDuration", "warmupPreferredDuration", "autoWarmupThreshold", [5, 30], 30)}
          {segment("cooldown", "Cool-down", "cooldownDuration", "cooldownPreferredDuration", "autoCooldownThreshold", [3, 10], 10)}
        </>
      );
      break;
    }

    case "rest":
      title = "Pace & rest";
      description = "Used in every generated session, whatever the style.";
      body = (
        <>
          <Field label="Between sets">
            <OptionList
              options={REST_STYLE_OPTIONS}
              value={draft.restStyle || DEFAULT_REST_STYLE}
              onChange={(v) => update({ restStyle: v })}
            />
          </Field>
          <Field label="Session pace">
            <OptionList
              options={SESSION_PACE_OPTIONS}
              value={draft.sessionPace || DEFAULT_SESSION_PACE}
              onChange={(v) => update({ sessionPace: v })}
            />
          </Field>
          <Field label="Rep speed" hint="Scales the time the Coach allows for every rep.">
            {isAuthenticated ? (
              <OptionList options={REP_SPEEDS} value={pace} onChange={setRepSpeed} />
            ) : (
              <p className="text-sm leading-snug text-muted-foreground">Sign in to set your rep speed.</p>
            )}
          </Field>
        </>
      );
      break;

    case "prefs": {
      title = "Preferences";
      description = "Taken as preferences, not rules. Today's notes and programmes come first.";
      const hidden = Array.isArray(draft.excludedSessionShapes) ? draft.excludedSessionShapes : [];
      body = (
        <>
          <Field
            label="Anything else"
            htmlFor="profile-preferences"
            hint="Injuries go under About you, where the AI treats them as rules."
          >
            <Textarea
              id="profile-preferences"
              value={draft.trainingPreferences || ""}
              onChange={(e) => update({ trainingPreferences: e.target.value })}
              maxLength={MAX_TRAINING_PREFERENCES_LENGTH}
              rows={4}
              placeholder="e.g. Love kettlebells, no burpees, prefer rowing to running"
            />
          </Field>
          {hidden.length > 0 && (
            <Field
              label="Hidden session shapes"
              hint="What's next and Surprise me won't pick these. Tap one to allow it again."
            >
              <div className="flex flex-wrap gap-1.5">
                {hidden.map((id) => (
                  <ChoiceChip
                    key={id}
                    selected={false}
                    onClick={() => update({ excludedSessionShapes: hidden.filter((h) => h !== id) })}
                    className="inline-flex items-center gap-1.5"
                  >
                    <span className="sr-only">Allow again: </span>
                    {archetypeName(id) || id}
                    <X className="h-3.5 w-3.5 opacity-70" />
                  </ChoiceChip>
                ))}
              </div>
            </Field>
          )}
        </>
      );
      break;
    }

    case "history": {
      title = "Recent history";
      const on = workoutSettings.guidedSessionEnabled ?? true;
      body = (
        <>
          <SwitchRow
            id="profile-history"
            label="Use recent workouts"
            description="Sends the AI a summary of your recent training, so it varies the work and doesn't overwork the same muscles."
            checked={on}
            onCheckedChange={(checked) => updateWorkoutSettings({ guidedSessionEnabled: checked })}
          />
          {on && (
            <DraggableStepper
              label="Look back"
              value={workoutSettings.historyLookbackDays ?? 7}
              onChange={(v) => updateWorkoutSettings({ historyLookbackDays: v })}
              min={3}
              max={30}
              unit="days"
            />
          )}
        </>
      );
      break;
    }

    case "changeovers":
      title = "Changeovers";
      description = "Time to move between exercises and between blocks, and whether the Coach waits for you. A place can set its own times.";
      body = (
        <>
          <Field hint="Between exercises in a block.">
            <DraggableStepper
              label="Between exercises"
              value={workoutSettings.defaultTransitionSeconds ?? 15}
              onChange={(v) => updateWorkoutSettings({ defaultTransitionSeconds: v })}
              min={5}
              max={60}
              step={5}
              unit="s"
            />
          </Field>
          <Field hint="Rack the bar, walk to the rower. Shown as a break in the plan.">
            <DraggableStepper
              label="Between blocks"
              value={workoutSettings.defaultBlockSetupSeconds ?? 45}
              onChange={(v) => updateWorkoutSettings({ defaultBlockSetupSeconds: v })}
              min={15}
              max={180}
              step={15}
              unit="s"
            />
          </Field>
          <Field
            label="Before timed work"
            hint="After a set of reps, before timed work: time to re-rack and get to the next station."
          >
            <OptionList
              options={CHANGEOVER_OPTIONS}
              value={workoutSettings.changeoverHold ?? "wait"}
              onChange={(v) => updateWorkoutSettings({ changeoverHold: v })}
            />
          </Field>
        </>
      );
      break;

    case "units": {
      // Site-wide units (#191): weights show in this; any weight field can
      // still take the other unit.
      title = "Units";
      description = "How weights show across Khronos. They're stored the same either way.";
      body = (
        <ChipGrid
          columns={2}
          options={[
            { value: "kg", label: "Kilograms (kg)" },
            { value: "lb", label: "Pounds (lb)" },
          ]}
          value={weightUnitOf(draft)}
          onChange={(v) => update({ weightUnit: v })}
        />
      );
      break;
    }

    case "weight": {
      // Body weight (#194): the VO2max estimate needs it.
      const unit = weightUnitOf(draft);
      const shown = toDisplayWeight(draft.bodyWeightKg || 75, unit);
      title = "Body weight";
      description = "Used to estimate your VO2max from a steady ride. Only you see it.";
      body = (
        <>
          <DraggableStepper
            label="Body weight"
            value={shown}
            onChange={(v) => update({ bodyWeightKg: fromDisplayWeight(v, unit) })}
            min={unit === "lb" ? 55 : 25}
            max={unit === "lb" ? 660 : 300}
            step={unit === "lb" ? 1 : 0.5}
            unit={unit}
          />
          <p className="flex justify-center">
            <UnitToggle value={unit} onChange={(u) => update({ weightUnit: u })} />
          </p>
          {draft.bodyWeightKg ? (
            <p className="text-center text-xs text-muted-foreground">
              <button type="button" className="underline underline-offset-2" onClick={() => update({ bodyWeightKg: 0 })}>
                Clear it
              </button>
            </p>
          ) : (
            <p className="text-center text-xs text-muted-foreground">Not set yet. Drag or type it in.</p>
          )}
        </>
      );
      break;
    }

    case "sex": {
      // For formulas that differ by sex, such as the 2k VO2max.
      title = "Sex";
      description = "Some estimates use a different formula for men and women, like your VO2max from a 2k. Only you see it.";
      body = isAuthenticated ? (
        <>
          <ChipGrid
            columns={2}
            options={[
              { value: "male", label: "Male" },
              { value: "female", label: "Female" },
            ]}
            value={sex}
            onChange={setSex}
          />
          {sex && (
            <p className="text-center text-xs text-muted-foreground">
              <button type="button" className="underline underline-offset-2" onClick={() => setSex(null)}>
                Clear it
              </button>
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Sign in to set it.</p>
      );
      break;
    }

    default:
      break;
  }

  return (
    <DetailSheet
      open={Boolean(kind)}
      onOpenChange={(open) => !open && close()}
      title={title}
      description={description}
      showDescription={Boolean(description)}
    >
      {body}
    </DetailSheet>
  );
};

export default ProfileSettingsSheet;
